import { useState, useRef } from "react";
import { toast } from "sonner";
import { UploadSimple, X, FileText, Image as ImageIcon, FilmStrip, CloudArrowUp } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";

/* LibraryUploadModal — three tabs, each with two sources:
     • Desktop (file picker + drag-drop)
     • Google Drive (paste share-link, only for files set "Anyone with the link")

   Calls the iter-41-r9 endpoints:
     POST /admin/social/library/upload-scripts   (JSON bulk)
     POST /admin/social/library/upload-image     (multipart)
     POST /admin/social/library/upload-video     (multipart)
     POST /admin/social/library/import-google-drive
*/
export default function LibraryUploadModal({ onClose, onUploaded }) {
  const [kind, setKind] = useState("image"); // image | video | script
  const [busy, setBusy] = useState(false);

  const TABS = [
    { id: "image",  label: "Image",  hint: "PNG / JPG / WEBP up to 12 MB.",  Icon: ImageIcon },
    { id: "video",  label: "Video",  hint: "MP4 up to 100 MB.",              Icon: FilmStrip },
    { id: "script", label: "Script (JSON)", hint: "Array of scene objects (same schema the workflow uploader uses).", Icon: FileText },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-background border hairline max-w-3xl w-full p-5 space-y-4 max-h-[92vh] overflow-y-auto"
        data-testid="library-upload-modal">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <div className="eyebrow">Library · Upload assets</div>
            <h2 className="font-serif text-2xl mt-1">Add external assets</h2>
            <p className="text-xs text-muted-foreground mt-2 max-w-2xl">
              Upload pre-made images, videos, or JSON-defined scripts straight into the
              library. Saved entries can be picked into any draft from the Workflow
              composer — zero LLM cost on re-apply.
            </p>
          </div>
          <button onClick={onClose} className="btn-ghost px-2 py-1.5 text-xs"
            aria-label="Close" data-testid="library-upload-close">
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* Kind tabs */}
        <div className="inline-flex rounded border hairline overflow-hidden text-xs"
          data-testid="library-upload-tabs">
          {TABS.map((t) => {
            const Icon = t.Icon;
            return (
              <button key={t.id}
                onClick={() => setKind(t.id)}
                disabled={busy}
                data-testid={`library-upload-tab-${t.id}`}
                className={`px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50 ${kind === t.id ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-muted/40"}`}>
                <Icon size={12} weight="duotone" /> {t.label}
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-muted-foreground italic">
          {TABS.find((t) => t.id === kind).hint}
        </p>

        <DesktopSource kind={kind} busy={busy} setBusy={setBusy} onUploaded={onUploaded} />
        <DriveSource   kind={kind} busy={busy} setBusy={setBusy} onUploaded={onUploaded} />
      </div>
    </div>
  );
}

/* ------------------------------- Desktop ------------------------------- */
function DesktopSource({ kind, busy, setBusy, onUploaded }) {
  const fileRef = useRef(null);
  const [name, setName] = useState("");
  const [tags, setTags] = useState("");
  const [imageKind, setImageKind] = useState("character");
  const [drag, setDrag] = useState(false);

  const accept = kind === "image" ? "image/*"
    : kind === "video" ? "video/mp4,video/*"
    : "application/json,.json";

  const handleFiles = async (files) => {
    if (!files?.length) return;
    const file = files[0];
    setBusy(true);
    try {
      if (kind === "script") {
        const text = await file.text();
        let arr;
        try { arr = JSON.parse(text); } catch (e) { toast.error(`Invalid JSON: ${e.message}`); return; }
        const scripts = Array.isArray(arr) ? arr : [arr];
        const { data } = await api.post("/admin/social/library/upload-scripts", {
          scripts: scripts.map((s) => ({ ...s, name: name || s.title || s.name || "" })),
        });
        toast.success(`Imported ${data.count} script${data.count === 1 ? "" : "s"}.`);
      } else {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("name", name);
        fd.append("tags", tags);
        if (kind === "image") fd.append("image_kind", imageKind);
        const endpoint = kind === "image" ? "/admin/social/library/upload-image"
                                          : "/admin/social/library/upload-video";
        const { data } = await api.post(endpoint, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`Uploaded ${(data.size_bytes / 1_048_576).toFixed(2)} MB to library.`);
      }
      setName(""); setTags("");
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <section className="card-flat p-4 space-y-3" data-testid="library-upload-desktop">
      <div className="eyebrow">From desktop</div>

      <div className="grid sm:grid-cols-2 gap-2">
        <label className="text-[11px]">
          <span className="text-muted-foreground">Name (optional)</span>
          <input value={name} onChange={(e) => setName(e.target.value)}
            disabled={busy}
            className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-0.5"
            data-testid="library-upload-name" placeholder="e.g. Skyline aerial · monsoon" />
        </label>
        {kind !== "script" && (
          <label className="text-[11px]">
            <span className="text-muted-foreground">Tags (comma-separated)</span>
            <input value={tags} onChange={(e) => setTags(e.target.value)}
              disabled={busy}
              className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-0.5"
              data-testid="library-upload-tags" placeholder="rent-vs-buy, monsoon" />
          </label>
        )}
        {kind === "image" && (
          <label className="text-[11px]">
            <span className="text-muted-foreground">Image kind</span>
            <select value={imageKind} onChange={(e) => setImageKind(e.target.value)}
              disabled={busy}
              className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-0.5"
              data-testid="library-upload-image-kind">
              <option value="character">Character</option>
              <option value="location">Location / background</option>
            </select>
          </label>
        )}
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault(); setDrag(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => !busy && fileRef.current?.click()}
        className={`border hairline ${drag ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-dashed"} p-6 text-center cursor-pointer transition-colors`}
        data-testid="library-upload-dropzone"
      >
        <UploadSimple size={24} weight="duotone" className="mx-auto mb-2 text-muted-foreground" />
        <div className="text-xs font-medium">
          {busy ? "Uploading…" : `Drop a ${kind} here or click to browse`}
        </div>
        <div className="text-[10px] text-muted-foreground mt-1">
          {kind === "image" && "PNG / JPG / WEBP · up to 12 MB"}
          {kind === "video" && "MP4 · up to 100 MB"}
          {kind === "script" && "JSON array of scene objects · up to 24 per file"}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept={accept}
          className="hidden"
          disabled={busy}
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="library-upload-input"
        />
      </div>
    </section>
  );
}

/* ----------------------------- Google Drive ----------------------------- */
function DriveSource({ kind, busy, setBusy, onUploaded }) {
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [imageKind, setImageKind] = useState("character");

  const submit = async () => {
    if (!url.trim()) { toast.error("Paste a Google Drive share URL first."); return; }
    setBusy(true);
    try {
      const body = { url: url.trim(), kind, name, image_kind: imageKind };
      const { data } = await api.post("/admin/social/library/import-google-drive", body);
      if (data.count != null) {
        toast.success(`Imported ${data.count} script${data.count === 1 ? "" : "s"} from Drive.`);
      } else {
        toast.success(`Imported ${(data.size_bytes / 1_048_576).toFixed(2)} MB from Drive.`);
      }
      setUrl(""); setName("");
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <section className="card-flat p-4 space-y-3" data-testid="library-upload-drive">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="eyebrow inline-flex items-center gap-1.5">
          <CloudArrowUp size={12} weight="duotone" /> From Google Drive
        </div>
        <div className="text-[10px] text-muted-foreground italic">
          File must be shared <b>“Anyone with the link can view.”</b>
        </div>
      </div>
      <label className="text-[11px] block">
        <span className="text-muted-foreground">Share URL</span>
        <input value={url} onChange={(e) => setUrl(e.target.value)}
          disabled={busy}
          placeholder="https://drive.google.com/file/d/<ID>/view?usp=sharing"
          className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-0.5 font-mono"
          data-testid="library-drive-url" />
      </label>
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="text-[11px]">
          <span className="text-muted-foreground">Name (optional)</span>
          <input value={name} onChange={(e) => setName(e.target.value)}
            disabled={busy}
            className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-0.5"
            data-testid="library-drive-name" />
        </label>
        {kind === "image" && (
          <label className="text-[11px]">
            <span className="text-muted-foreground">Image kind</span>
            <select value={imageKind} onChange={(e) => setImageKind(e.target.value)}
              disabled={busy}
              className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-0.5"
              data-testid="library-drive-image-kind">
              <option value="character">Character</option>
              <option value="location">Location / background</option>
            </select>
          </label>
        )}
      </div>
      <div className="flex items-center justify-end">
        <button onClick={submit} disabled={busy || !url.trim()}
          className="btn-primary px-4 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="library-drive-import">
          <CloudArrowUp size={12} weight="bold" />
          {busy ? "Importing…" : "Import from Drive"}
        </button>
      </div>
      <p className="text-[10px] text-muted-foreground">
        Tip: Drive blocks direct download for files larger than ~100 MB without a virus-scan confirmation —
        for those, use the desktop uploader instead.
      </p>
    </section>
  );
}
